import { useId, useState, type FormEvent, type KeyboardEvent } from "react";
import { formatMoney, type SearchSuggestion } from "@neus/shared";
import { useSearchSuggestions } from "../../api/hooks";
import styles from "./SearchBox.module.css";

interface Props {
  /** Called with the trimmed text when the shopper submits without picking a suggestion. */
  onSubmit?: (q: string) => void;
  /** Called when a suggestion is chosen (click, or Enter on the highlighted one). */
  onPick: (suggestion: SearchSuggestion) => void;
  /** Escape with no suggestions open. */
  onEscape?: () => void;
  placeholder?: string;
  label?: string;
  autoFocus?: boolean;
  /** Text of the submit button; omit to hide it. */
  submitLabel?: string;
  /** Clear the text after a pick (e.g. when adding lines to a list). */
  clearOnPick?: boolean;
}

/**
 * Search input with type-ahead suggestions, following the ARIA combobox pattern:
 * Up/Down move through suggestions, Enter picks the highlighted one (or submits), and
 * Escape closes the list.
 */
export function SearchBox({
  onSubmit,
  onPick,
  onEscape,
  placeholder = "Search tools, paint, hardware, SKUs…",
  label = "Search products",
  autoFocus,
  submitLabel,
  clearOnPick,
}: Props) {
  const id = useId();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const { data, isFetching } = useSearchSuggestions(q);
  // Results for an earlier term stay visible only while the shopper refines that same term
  // ("dri" → "dril"), never for an unrelated one, so Enter can't pick a stale product.
  const typed = q.trim().toLowerCase();
  const suggestions = typed.length >= 2 && data && typed.startsWith(data.term.toLowerCase()) ? data.items : [];
  // When the list itself changes (e.g. a late response reorders it), drop the highlight so Enter
  // can't pick a product that slid into the highlighted row.
  const listKey = suggestions.map((x) => x.sku).join("|");
  const [highlightedList, setHighlightedList] = useState(listKey);
  if (listKey !== highlightedList) {
    setHighlightedList(listKey);
    setActive(-1);
  }
  const expanded = open && suggestions.length > 0;
  const optionId = (i: number) => `${id}-option-${i}`;

  const pick = (s: SearchSuggestion) => {
    onPick(s);
    setOpen(false);
    setActive(-1);
    if (clearOnPick) setQ("");
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (expanded && active >= 0 && suggestions[active]) return pick(suggestions[active]);
    const text = q.trim();
    if (text && onSubmit) {
      setOpen(false);
      onSubmit(text);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (!suggestions.length) return;
      e.preventDefault();
      setOpen(true);
      // -1 is "no suggestion highlighted" (the typed text); the highlight wraps through it.
      const n = suggestions.length;
      setActive((i) => (e.key === "ArrowDown" ? (i + 1 >= n ? -1 : i + 1) : i - 1 < -1 ? n - 1 : i - 1));
    } else if (e.key === "Escape") {
      if (expanded) {
        e.preventDefault();
        setOpen(false);
        setActive(-1);
      } else onEscape?.();
    }
  };

  return (
    <form className={styles.form} role="search" onSubmit={submit}>
      <div className={styles.field}>
        <label htmlFor={`${id}-input`} className="visually-hidden">
          {label}
        </label>
        <input
          id={`${id}-input`}
          className={styles.input}
          type="search"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={`${id}-listbox`}
          aria-activedescendant={expanded && active >= 0 ? optionId(active) : undefined}
          aria-busy={isFetching}
          autoComplete="off"
          autoFocus={autoFocus}
          placeholder={placeholder}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
        />
        <ul id={`${id}-listbox`} role="listbox" aria-label="Suggestions" className={styles.list} hidden={!expanded}>
          {suggestions.map((s, i) => (
            <li
              key={s.sku}
              id={optionId(i)}
              role="option"
              aria-selected={i === active}
              className={i === active ? `${styles.option} ${styles.active}` : styles.option}
              // Keep focus in the input so the pick happens before blur closes the list.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(s)}
              onMouseEnter={() => setActive(i)}
            >
              {s.image ? <img src={s.image} alt="" className={styles.thumb} /> : <span className={styles.thumb} />}
              <span className={styles.text}>
                <span className={styles.name}>{s.name}</span>
                <span className={styles.meta}>
                  {s.brand} · {s.sku}
                  {s.inventory <= 0 && <span className={styles.soldOut}> · Sold out</span>}
                </span>
              </span>
              <span className={styles.price}>{formatMoney(s.price)}</span>
            </li>
          ))}
        </ul>
      </div>
      {submitLabel && (
        <button className="btn" type="submit">
          {submitLabel}
        </button>
      )}
    </form>
  );
}
